
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { add } from 'lodash';
import { DateTime } from 'luxon';
import { ValidationObserver } from 'vee-validate';

import { sharedState } from '../../../../../framework/state';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import TextField from '../../../../../framework/fields/TextField.vue';
import MonthField from '../../../../../framework/fields/MonthField.vue';
import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';

import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { endAndMoveVehicleContract, endVehicleContract } from '../../../../client/vehicle_contract_client';
import { convertLocalDateToUtc, formatCurrency, formatToFinnishDate } from '../../../../service/helper_service';
import { getCustomerOptions } from '../../../../service/application_options_service';
import { getCustomerGroupCustomers } from '../../../../client/customer_client';
import { getVehicleCostMonthlyRows } from '../../../../client/vehicle_cost_monthly_row_client';
import { ExtendedVehicleCostMonthlyRow } from '../../../../../../common/application/model/VehicleCostMonthlyRow';
import { dateToDbDateString, dateToDbMonthDateString } from '../../../../../../common/framework/util/convert';
import { MoveVehicleContractReq } from '../../../../../../common/application/model/api/MoveVehicleContractReq';

@Component({
    components: {
        SelectField,
        TextField,
        MonthField,
        AutoCompleteMultipleField,
        ValidationObserver,
        SwitchField,
    },
})
export default class EndAndMoveVehicleContractWizard extends Vue {
    readonly resourceType = ApplicationResource.CUSTOMER;

    @Prop(Object) readonly selectedContract!: VehicleContract;

    // State
    shared = sharedState;
    local = {
        activeStep: 0,
        ableToMove: false,
        error: '',
        row: {
            newCustomerId: '',
            endDate: dateToDbDateString(DateTime.now().endOf('month').toJSDate()),
            generateBalanceInvoice: true,
            transferBudget: false,
            invoiceDate: dateToDbDateString(DateTime.now().endOf('month').toJSDate()),
        },
        subcustomersOnly: true,
        vehicleMonthlyCost: { invoiced: 0, paid: 0, balance: 0 } as {
            invoiced: number;
            paid: number;
            balance: number;
        },
        loading: true,
        customerOptions: [] as { id: string | undefined; label: string }[],
        subcustomerOptions: [] as { id: string | undefined; label: string }[],
        selectedCustomerId: undefined as string | undefined,
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        this.local.customerOptions = await getCustomerOptions();
        this.local.subcustomerOptions = await getCustomerGroupCustomers(this.selectedContract.customerId);

        if (this.selectedContract.customerId) {
            const currentCustomer = this.local.customerOptions.find((c) => c.id === this.selectedContract.customerId);

            if (currentCustomer) {
                const currentCustomerIndex = this.local.customerOptions.indexOf(currentCustomer);

                if (currentCustomerIndex > -1) {
                    this.local.customerOptions.splice(currentCustomerIndex, 1);
                }
            }
        }

        if (this.selectedContract.customerId) {
            this.local.ableToMove = true;
        } else {
            this.local.error = this.$t('error.noCompanyToMoveContract').toString();
        }

        this.local.loading = false;
    }

    prevStep() {
        const tmp = this.local.activeStep - 1;
        this.local.activeStep = tmp >= 0 ? tmp : 0;
    }

    nextStep() {
        this.local.activeStep += 1;
    }

    @Watch('local.row.generateBalanceInvoice')
    addBalanceInvoiceChange(newValue: boolean) {
        this.local.row.transferBudget = !newValue;
    }

    @Watch('local.row.transferBudget')
    transferBudgetChange(newValue: boolean) {
        this.local.row.generateBalanceInvoice = !newValue;
    }

    @Watch('local.activeStep')
    async getSummary(step: number) {
        if (step === 1) {
            this.local.loading = true;
            const params = new Map<string, string>([
                ['vehicleContractId', this.selectedContract.id],
                [
                    'month',
                    `${dateToDbMonthDateString(
                        DateTime.fromFormat(this.local.row.endDate, 'yyyy-MM-dd').endOf('month').toJSDate(),
                    )}`,
                ],
                ['pageSize', '1000'],
            ]);
            const monthlyRows = await getVehicleCostMonthlyRows(params);
            this.calculateMonthlyCosts(monthlyRows.vehicleCostMonthlyRows);
            this.local.loading = false;
        }
    }
    get maxDate(): Date {
        return DateTime.fromJSDate(new Date(this.selectedContract.smartFleetContractEnd)).endOf('month').toJSDate();
    }

    get minDate(): Date {
        return DateTime.now().endOf('month').toJSDate();
    }

    private calculateMonthlyCosts(vehicleMonthlyCosts: ExtendedVehicleCostMonthlyRow[]) {
        this.local.vehicleMonthlyCost = vehicleMonthlyCosts.reduce(
            (costs, currentMonthlyCost) => {
                costs.paid = currentMonthlyCost.paid ? add(currentMonthlyCost.paid, costs.paid) : costs.paid;
                costs.invoiced = currentMonthlyCost.invoiced
                    ? add(currentMonthlyCost.invoiced, costs.invoiced)
                    : costs.invoiced;
                costs.balance = add(currentMonthlyCost.balance, costs.balance);
                return costs;
            },
            { paid: 0, invoiced: 0, balance: 0 },
        );
    }

    confirmEndAndMoveContract() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmEndContract').toString(),
            message: this.$t('message.confirmEndContractMessage').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => this.endAndMoveContract(),
        });
    }

    async endAndMoveContract() {
        const request: MoveVehicleContractReq = {
            addBalanceInvoice: this.local.row.generateBalanceInvoice,
            contractId: this.selectedContract.id,
            newCustomerId: this.local.selectedCustomerId!,
            transferBudget: false,
            transferDate: convertLocalDateToUtc(new Date(this.local.row.endDate)),
            userId: this.shared.context.userId,
        };

        const { contractEndedAndMoved } = await endAndMoveVehicleContract(request);
        if (contractEndedAndMoved) {
            this.$emit('contractMovedAndEnded');
        }
    }

    formatDate(date: Date | undefined): string {
        if (!date) {
            return '';
        }

        return formatToFinnishDate(new Date(date).toISOString());
    }

    formatCurrencyValue(value: number | null | undefined) {
        return formatCurrency(value);
    }
}
